import { createAsyncThunk } from '@reduxjs/toolkit'
import api, { api2 } from '../../services'
import {
  GetTestAverageOptions,
  GetTestAverageResponse,
  UpdateTestOptions,
  TestPaperDetails,
  SaveAnswerPayload,
  Question,
  AnsweredTestResultsResponse,
  GetScheuledTestsState,
  GetAttemptedTestsState,
  ResportQuestionPayload,
  AssignmentProps
} from './type'
import apiEndpoints from '../../const/apiEndpoints'
import { RootState } from '../store'
import history from '../../const/history'
import ROUTES from '../../const/routes'
import { updateIsMenuOpen } from '../../redux/menu/actions'
import formatDate from '../../helpers/formatDate'

export const getTestAverage = createAsyncThunk(
  'test/average',
  async ({
    batchId,
    studentId
  }: GetTestAverageOptions): Promise<GetTestAverageResponse> => {
    const response = await api.get(
      `${apiEndpoints.averageBatchTests}?batchId=${batchId}&studentId=${studentId}`
    )
    return response.data
  }
)

export const getScheuledTests = createAsyncThunk(
  'test/scheduled',
  async ({
    batchId,
    studentId
  }: GetTestAverageOptions): Promise<GetScheuledTestsState> => {
    const response = await api.get(
      `${apiEndpoints.scheduleTestsForStudent}?batchId=${batchId}&studentId=${studentId}`
    )
    return {
      scheuledTests: response.data?.MOCK_TEST,
      practiceTests: response.data?.PRACTICE,
      fullLengthTests: response.data?.FULL_LENGTH,
      sectionWiseTests: response.data?.SECTION_WISE,
      chapterWiseTests: response.data?.CHAPTER_WISE
    }
  }
)

export const getAttemptedTests = createAsyncThunk(
  'test/attempted',
  async ({
    batchId,
    studentId
  }: GetTestAverageOptions): Promise<GetAttemptedTestsState> => {
    const response = await api.get(
      `${apiEndpoints.attemptedTestsForStudent}?batchId=${batchId}&studentId=${studentId}`
    )
    return {
      attemptedTests: response.data?.MOCK_TEST,
      attemptedPracticeTests: response.data?.PRACTICE,
      attemptedfullLengthTests: response.data?.FULL_LENGTH,
      attemptedchapterWiseTests: response.data?.CHAPTER_WISE,
      attemptedsectionWiseTests: response.data?.SECTION_WISE
    }
  }
)

export const updateTestStatus = createAsyncThunk(
  'test/updateStatus',
  async (
    { batchId, studentId, testPaperId, status }: UpdateTestOptions,
    { dispatch }
  ): Promise<GetTestAverageResponse> => {
    const data = {
      testPaperId: testPaperId,
      batchId: batchId,
      studentId: studentId,
      status: status === 'STARTED' ? 'PENDING' : 'STARTED'
    }

    const response = await api.post(apiEndpoints.testStatus, data)
    await dispatch(
      getScheuledTests({
        batchId: batchId,
        studentId: studentId
      })
    )
    if (status !== 'STARTED') {
      dispatch(updateIsMenuOpen(false))
      await dispatch(
        testPaperForStudent({ testPaperId: testPaperId, studentId: studentId })
      )
      await dispatch(
        getStudentTestPaper({ testPaperId: testPaperId, studentId: studentId })
      )
    }
    return response.data?.data
  }
)

export const testPaperForStudent = createAsyncThunk(
  'test/testPaperForStudent',
  async ({
    studentId,
    testPaperId
  }: TestPaperDetails): Promise<GetTestAverageResponse> => {
    const response = await api.get(
      `${apiEndpoints.testPaperForStudent}?testPaperId=${testPaperId}&userDetailId=${studentId}`
    )

    return response.data?.data
  }
)

export const getStudentTestPaper = createAsyncThunk(
  'test/getStudentTestPaper',
  async ({
    studentId,
    testPaperId
  }: TestPaperDetails): Promise<GetTestAverageResponse> => {
    const response = await api.get(
      `${apiEndpoints.getStudentTestPaper}?testPaperId=${testPaperId}&studentId=${studentId}`
    )

    return response.data
  }
)

export const saveAnswer = createAsyncThunk(
  'test/next',
  async ({
    studentId,
    testPaperId,
    answer,
    attempt,
    questionPaperId,
    testDurationTime,
    timeSpent
  }: SaveAnswerPayload): Promise<string> => {
    const data = {
      studentId,
      testPaperId,
      answer,
      attempt,
      questionPaperId,
      testDurationTime,
      timeSpent
    }
    const response = await api.post(`${apiEndpoints.saveAnswer}`, data)

    return response.data
  }
)

export const answeredTestPaper = createAsyncThunk(
  'test/answeredTestPaper',
  async ({
    studentId,
    testPaperId
  }: TestPaperDetails): Promise<AnsweredTestResultsResponse> => {
    const data = {
      attempt: 1,
      studentId: studentId,
      testPaperId: testPaperId
    }

    const response = await api.post(`${apiEndpoints.answeredTestPaper}`, data)

    if (response?.data) {
      history.push(ROUTES.RESULTS)
    }

    return {
      answeredTestResult: response.data,
      selectedTestPaperId: testPaperId
    }
  }
)

export const submitTestPaper = createAsyncThunk(
  'test/submitTestPaper',
  async (_undefined, { getState }): Promise<string> => {
    const {
      test: { testPaper, testPaperDetails },
      user: { userInfo }
    } = getState() as RootState

    const testPayload = testPaper?.quesionList.map((qtn: Question) => {
      return {
        answer: qtn?.answer || '',
        questionPaperId: qtn?.id,
        timeSpent: 3
      }
    })

    const data = {
      attempt: 1,
      studentId: userInfo?.userDetail?.usersId,
      questionAnswerList: testPayload,
      testDurationTime: testPaperDetails?.testPaper?.duration,
      testPaperId: testPaperDetails?.testPaper?.id
    }

    const response = await api.post(`${apiEndpoints.submitTestPaper}`, data)

    if (response?.data) {
      history.push(ROUTES.ASSESSMENT)
    }

    return response.data
  }
)

export const reportQuestion = createAsyncThunk(
  'test/reportQuestion',
  async (
    { questionPaperId, answer, reportedAnswer }: ResportQuestionPayload,
    { getState }
  ): Promise<any> => {
    const {
      user: { userInfo, selectedBatch },
      test: { selectedTestPaperId }
    } = getState() as RootState

    const usersId = userInfo?.userDetail?.usersId
    const data = {
      questionPaperId: questionPaperId,
      answer: answer,
      reportedAnswer: reportedAnswer,
      batchId: selectedBatch,
      status: 'REPORTED',
      studentId: usersId,
      testPaperId: selectedTestPaperId
    }

    const response = await api.post(`${apiEndpoints.reportQuestion}`, data)
    return response ? questionPaperId : ''
  }
)

export const addAssignment = createAsyncThunk(
  'test/addAssignment',
  async (
    { batchId, title, description, date, files }: AssignmentProps,
    { getState }
  ): Promise<void> => {
    const {
      user: { userInfo }
    } = getState() as RootState

    const formData = new FormData()
    formData.append('files', files)
    formData.append('title', title)
    formData.append('description', description)
    formData.append('subject', userInfo?.userDetail?.subject || '')
    formData.append('batchId', batchId)
    formData.append('date', formatDate(date))
    formData.append('teacherName', userInfo?.userDetail.userName || '')

    const response = await api.post(`${apiEndpoints.addAssignment}`, formData)
    console.log(response)
  }
)

export const dailyTestApi = createAsyncThunk(
  'test/dailyTest',
  async (requestPayload: {
    type: string
    id: any
  }): Promise<GetTestAverageResponse> => {
    const response = await api2.post(
      `${apiEndpoints.dailyTest} `,
      requestPayload
    )
    return response.data?.data
  }
)
